import { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronsUpDown } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";

import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from "src/components/ui/responsive-dialog";
import { ScrollArea, ScrollBar } from "src/components/ui/scroll-area";
import { marketingIntegrationTypes } from "src/lib/constants";
import { useApiMutation, useApiQuery } from "src/lib/hooks/useApi";
import {
  CAMPAIGN_GROUPS_QUERY,
  CAMPAIGNS_QUERY,
  DELETE_CAMPAIGN_GROUP_MUTATION,
  UPSERT_CAMPAIGN_GROUP_MUTATION,
} from "src/lib/queries/marketingActivity";
import { cn } from "src/lib/utils";

const groupFormSchema = z.object({
  name: z.string().min(1, { message: "Group name is required" }),
  campaigns: z.array(z.string()).min(1, {
    message: "At least one campaign must be selected",
  }),
});

export function CampaignGroupsDialog({
  integrationId,
  marketingIntegrationType,
  onUpdate,
}) {
  const {
    data: campaignGroupsResponse,
    queryApi: queryCampaignGroups,
    loading: loadingCampaignGroups,
  } = useApiQuery({
    query: CAMPAIGN_GROUPS_QUERY,
  });
  const {
    data: campaignsResponse,
    queryApi: queryCampaigns,
    loading: loadingCampaigns,
  } = useApiQuery({
    query: CAMPAIGNS_QUERY,
  });
  const [selectedGroup, setSelectedGroup] = useState();
  const groupMutationMode = selectedGroup
    ? selectedGroup?.id
      ? "edit"
      : "create"
    : null;

  const [groupFormLoading, setGroupFormLoading] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const queryGroupsAndCampaigns = () => {
    const type = marketingIntegrationType
      ? marketingIntegrationType.id.toUpperCase()
      : null;

    queryCampaignGroups({
      integrationId,
      marketingIntegrationType: type,
    });
    queryCampaigns({
      integrationId,
      marketingIntegrationType: type,
    });
  };

  useEffect(() => {
    queryGroupsAndCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, marketingIntegrationType]);

  const loading = loadingCampaignGroups || loadingCampaigns || groupFormLoading;

  // Content shown when no group is selected
  const groupListContent = useMemo(() => {
    if (selectedGroup) return null;

    return campaignGroupsResponse?.campaignGroups?.map((group, index) => {
      const campaigns = [...(group.campaigns || [])].splice(0, 5);
      const remainder = group.campaigns?.length - campaigns.length;

      return (
        <button
          key={index}
          onClick={() => {
            setSelectedGroup(group);
          }}
          disabled={loading}
          className={cn(
            "bg-card hover:bg-card/80 flex flex-col justify-between gap-y-4 rounded-lg border p-4 transition-colors",
            loading ? "opacity-50" : "cursor-pointer"
          )}
        >
          <span className="mr-auto font-semibold">{group.name || "Group"}</span>

          <div className="flex flex-wrap gap-2">
            {campaigns.length ? (
              <>
                {campaigns.map((campaign, idx) => {
                  const name = campaign.name || "Unknown Campaign";

                  return (
                    <Badge
                      variant="secondary"
                      key={idx}
                      className="max-w-full rounded-full p-2"
                    >
                      <span
                        className={cn(
                          campaign.active
                            ? "text-chart-good"
                            : "text-muted-foreground",
                          "mr-2"
                        )}
                      >
                        ●
                      </span>

                      <span className="overflow-hidden overflow-ellipsis whitespace-nowrap">
                        {name}
                      </span>
                    </Badge>
                  );
                })}

                {remainder > 0 && (
                  <Badge variant="secondary" className="rounded-full p-2">
                    +{remainder}
                  </Badge>
                )}
              </>
            ) : (
              <span className="text-muted-foreground">No campaigns</span>
            )}
          </div>
        </button>
      );
    });
  }, [campaignGroupsResponse, selectedGroup, loading]);

  return (
    <>
      <ResponsiveDialog
        open={dialogOpen}
        onOpenChange={(state) => {
          if (dropdownOpen) {
            return; // prevent closing dialog when dropdown is open
          }

          setDialogOpen(state);

          if (!state) {
            setSelectedGroup(null);
          }
        }}
      >
        <ResponsiveDialogTrigger asChild>
          <Button disabled={loading} size="sm">
            Campaign Groups
          </Button>
        </ResponsiveDialogTrigger>

        <ResponsiveDialogContent
          className={cn("flex flex-col", !selectedGroup && "h-full sm:h-auto")}
        >
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="items-baseline">
              {selectedGroup
                ? groupMutationMode === "edit"
                  ? "Edit Group"
                  : "Create Group"
                : "Campaign Groups"}

              {marketingIntegrationTypes && (
                <span className="text-muted-foreground ml-2 text-xs font-normal">
                  {marketingIntegrationTypes[
                    marketingIntegrationType?.id?.toLowerCase()
                  ]?.name || "All Integrations"}
                </span>
              )}
            </ResponsiveDialogTitle>
            {!loading &&
              !selectedGroup &&
              !campaignGroupsResponse?.campaignGroups?.length && (
                <ResponsiveDialogDescription>
                  <span className="my-8 block w-full text-center">
                    No Campaign Groups
                  </span>
                </ResponsiveDialogDescription>
              )}
          </ResponsiveDialogHeader>

          <ScrollArea className="-mr-3 pr-3">
            <div className="flex flex-col space-y-4 p-px sm:max-h-[60vh]">
              {groupListContent}

              {selectedGroup && (
                <GroupForm
                  group={selectedGroup}
                  integrationId={integrationId}
                  campaigns={campaignsResponse?.campaigns}
                  setLoading={setGroupFormLoading}
                  afterSubmit={() => {
                    setSelectedGroup(null);
                    setGroupFormLoading(false);

                    queryGroupsAndCampaigns(); // refresh list
                    onUpdate?.(); // update parent component
                  }}
                  dropdownOpen={dropdownOpen}
                  setDropdownOpen={setDropdownOpen}
                />
              )}
            </div>

            <ScrollBar />
          </ScrollArea>

          <ResponsiveDialogFooter className="flex-row flex-wrap">
            {groupMutationMode === "edit" && (
              <Button
                className="sm:mr-auto"
                variant="destructive"
                disabled={loading}
                onClick={() => {
                  setGroupToDelete(selectedGroup);
                }}
              >
                Delete
              </Button>
            )}

            <ResponsiveDialogClose
              asChild
              onClick={(e) => {
                if (selectedGroup) {
                  e.preventDefault();
                  setSelectedGroup(null);
                }
              }}
            >
              <Button
                variant="secondary"
                disabled={loading}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
            </ResponsiveDialogClose>

            {selectedGroup && (
              <Button
                className="grow sm:grow-0"
                form="campaign-group-form"
                type="submit"
                disabled={loading}
              >
                Save
              </Button>
            )}

            {!selectedGroup && (
              <Button
                className="w-full sm:ml-auto sm:w-auto"
                disabled={loading}
                onClick={() => {
                  setSelectedGroup({
                    marketingIntegrationType:
                      marketingIntegrationType?.id?.toUpperCase() || null,
                  });
                }}
              >
                Create Group
              </Button>
            )}
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      <GroupDeleteDialog
        group={groupToDelete}
        integrationId={integrationId}
        onClose={() => {
          setGroupToDelete(null);
        }}
        onDelete={() => {
          setSelectedGroup(null);
          queryGroupsAndCampaigns(); // refresh list
          onUpdate?.(); // update parent component
        }}
      />
    </>
  );
}

function GroupForm({
  group,
  integrationId,
  campaigns,
  setLoading,
  afterSubmit,
  dropdownOpen,
  setDropdownOpen,
}) {
  const form = useForm({
    resolver: zodResolver(groupFormSchema),
    defaultValues: {
      name: group?.name || "",
      campaigns: group?.campaigns?.map((c) => c.id) || [],
    },
  });
  const { mutationApi: upsertGroup, loading } = useApiMutation({
    mutation: UPSERT_CAMPAIGN_GROUP_MUTATION,
  });

  useEffect(() => {
    if (setLoading) {
      setLoading(loading);
    }
  }, [loading, setLoading]);

  const onSubmit = async (data) => {
    const toastId = toast.loading(
      group?.id ? "Updating group..." : "Creating group..."
    );

    const { data: res } = await upsertGroup({
      integrationId: integrationId,
      id: group?.id,
      marketingIntegrationType: group?.marketingIntegrationType,
      ...data,
    });

    if (res) {
      toast.success(
        group?.id
          ? "Group updated successfully!"
          : "Group created successfully!",
        { id: toastId }
      );

      afterSubmit?.();
    } else {
      toast.error("Failed to save group", { id: toastId });
    }
  };

  return (
    <Form {...form}>
      <form
        id="campaign-group-form"
        className="space-y-6"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="name"
          disabled={loading}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter group name" />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="campaigns"
          disabled={loading}
          render={({ field }) => {
            return (
              <FormItem className="flex flex-col">
                <FormLabel>Campaigns</FormLabel>
                <Popover
                  modal={true}
                  open={dropdownOpen}
                  onOpenChange={setDropdownOpen}
                >
                  <PopoverTrigger asChild inert={dropdownOpen}>
                    <FormControl>
                      <Button
                        variant="outline"
                        role="combobox"
                        disabled={loading}
                        className={cn(
                          "w-full justify-between",
                          !field.value.length && "text-muted-foreground"
                        )}
                      >
                        {field.value.length
                          ? field.value.length > 1
                            ? `${field.value.length} campaigns selected`
                            : campaigns.find((c) => c.id === field.value[0])
                                ?.name
                          : "Select campaigns"}
                        <ChevronsUpDown className="opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-[calc(100vw-var(--spacing)*8)] p-0 sm:w-[460px]">
                    <Command>
                      <CommandInput
                        placeholder="Search campaigns..."
                        className="h-9"
                      />
                      <CommandList>
                        <ScrollArea
                          type="always"
                          className="flex max-h-72 flex-col pr-2"
                        >
                          <CommandEmpty>No campaign found.</CommandEmpty>
                          <CommandGroup>
                            {campaigns
                              .filter((campaign) => {
                                // Only show campaigns that are not part of any group
                                return (
                                  !campaign.campaignGroups?.length ||
                                  campaign.campaignGroups.some(
                                    (g) => g.id === group?.id
                                  )
                                );
                              })
                              .map((campaign) => (
                                <CommandItem
                                  value={campaign.name}
                                  key={campaign.id}
                                  onSelect={() => {
                                    const exists = field.value.includes(
                                      campaign.id
                                    );

                                    form.setValue("campaigns", [
                                      ...field.value.filter(
                                        (id) => id !== campaign.id
                                      ),
                                      ...(exists ? [] : [campaign.id]),
                                    ]);
                                  }}
                                >
                                  <span
                                    className={cn(
                                      campaign.active
                                        ? "text-chart-good"
                                        : "text-muted-foreground",
                                      "mr-2"
                                    )}
                                  >
                                    ●
                                  </span>
                                  {campaign.name}
                                  <Check
                                    className={cn(
                                      "ml-auto",
                                      field.value.includes(campaign.id)
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                </CommandItem>
                              ))}
                          </CommandGroup>
                          <ScrollBar />
                        </ScrollArea>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {group?.marketingIntegrationType && (
                  <FormDescription>
                    Only showing campaigns for{" "}
                    {
                      marketingIntegrationTypes[
                        group.marketingIntegrationType.toLowerCase()
                      ].name
                    }{" "}
                    integration(s)
                  </FormDescription>
                )}

                <FormMessage />
              </FormItem>
            );
          }}
        />
      </form>
    </Form>
  );
}

function GroupDeleteDialog({ group, integrationId, onClose, onDelete }) {
  const { mutationApi: deleteGroup, loading } = useApiMutation({
    mutation: DELETE_CAMPAIGN_GROUP_MUTATION,
    onSuccess: () => {
      onClose?.();
      onDelete?.();
    },
    onError: () => "Failed to delete campaign group",
  });

  const handleDelete = async () => {
    const toastId = toast.loading(`Deleting campaign group "${group.name}"`);

    const { data } = await deleteGroup({
      id: group.id,
      integrationId,
      marketingIntegrationType: group.marketingIntegrationType,
    });

    if (data) {
      toast.success(`Campaign group "${group.name}" deleted successfully`, {
        id: toastId,
      });
    } else {
      toast.error(`Failed to delete campaign group "${group.name}"`, {
        id: toastId,
      });
    }
  };

  return (
    <ResponsiveDialog open={!!group} onOpenChange={onClose}>
      <ResponsiveDialogContent className="h-auto">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Delete Campaign Group</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            Are you sure you want to delete the campaign group &quot;
            {group?.name}&quot;? This action cannot be undone.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        <ResponsiveDialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
